import React from 'react';
import { FormState, Form as FormStateForm } from 'react-formstate';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import WiredInput from '../components/WiredInput';
import { focusOnFirstInvalid, populateFormErrors } from '../../utils/validation';
import ValidationMessage from '../components/ValidationMessage';
import T from '../components/T';
import DonationCounter from './DonationCounter';
import Label from '../components/Label';
import EstCounter from '../components/EstCounter';

export default class PriceForm extends React.Component {
  static contextTypes = {
    formatters: PropTypes.object
  };

  props: {
    campaignId: number|string,
    donated: number,
    donationGoal: number,
    est: number,
    allowMonthly: boolean
  };

  constructor(props) {
    super(props);
    this.formState = FormState.create(this);
    this.state = this.formState.injectModel(props.form);
    if (this.props.campaignId === '3a15614c-0fee-496f-b93d-bff0039f034b') {
      this.predefinedPrices = [30, 100, 200];
    } else if (this.props.campaignId === '680a16e7-e88e-484b-bbed-c49372820abd') {
      this.predefinedPrices = [30, 200, 500];
    } else if (this.props.campaignId === '958c9bc2-e2a0-4d3a-85cb-2db9cbb93b40') {
      this.predefinedPrices = [30, 50, 100];
    } else {
      this.predefinedPrices = [20, 30, 50];
    }
  }

  componentDidUpdate() {
    // reflect form height
    const { parentForceRerender } = this.props;
    parentForceRerender();
  }

  selectCustomPrice = () => {
    const context = this.formState.createUnitOfWork();
    const fieldState = context.getFieldState('price');
    fieldState.setValue('custom');
    context.updateFormState();
  };

  onPriceLabelClick = (e, value) => {
    const context = this.formState.createUnitOfWork();
    context.set('price', value).validate();

    if (value !== 'custom') {
      context.getFieldState('customPrice').setValid(); // hack-ish
    }

    context.updateFormState();
  };

  onMonthlyLabelClick = (e) => {
    // event is fired twice, for checkbox and for label. Ignore one of them.
    if (e.target.tagName === 'INPUT') return;

    const context = this.formState.createUnitOfWork();
    const fieldState = context.getFieldState('isMonthlyDonation');
    fieldState.setValue(+fieldState.getValue('isMonthlyDonation') ^ 1).validate();
    context.updateFormState();
  };

  onInputChange = ({ target: { name, value } }) => {
    const context = this.formState.createUnitOfWork();
    context.set(name, value).validate();
    context.updateFormState();
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { onSubmit } = this.props;

    const model = this.formState.createUnitOfWork().createModel();

    if (model) {
      onSubmit(e, model);
    } else {
      focusOnFirstInvalid(this.formState, this);
    }
  };

  render() {
    const { formDisabled, donated, endsAt, donationGoal, progress, allowMonthly } = this.props;
    const { formatters } = this.context;
    const errors = populateFormErrors(this.formState);

    return (
      <section className="donation">
        <FormStateForm formState={this.formState} className="c-floating__body" onSubmit={this.onSubmit}>
          <fieldset disabled={formDisabled}>
            <DonationCounter donated={donated} goal={donationGoal} progress={progress} />

            <div className="estimation donation__est text-icon">
              <i className="icon-time text-icon__icon" />
              <span className="text-icon__text">
                <EstCounter deadline={endsAt} />
              </span>
            </div>
            <div className="donation__cfloating-padding">
              <div className="donation__price-buttons">
                {this.predefinedPrices.map(price => (
                  <label
                    key={price}
                    className="donation__price-button-width"
                    onClick={e => this.onPriceLabelClick(e, price)}
                  >
                    <WiredInput
                      type="radio"
                      name="price"
                      value={price}
                      className="donation__price-radio"
                    />
                    <button type="button" className="c-button donation__price-button">
                      <span className="c-button__content">{formatters.formatCurrency(price, '€', 0)}</span>
                    </button>
                  </label>
                ))}
              </div>

              <label className="c-input__wrapper donation__custom-price">
                <span className={classNames(
                  'c-input__border', { 'c-input__border--error': errors.customPrice }
                )}>
                  <span className="c-input__flag">
                    <T msg="donation.labels.customPrice" />
                  </span>
                  <span className="c-input__flag">€</span>
                  <WiredInput
                    type="radio"
                    name="price"
                    value="custom"
                    className="donation__price-radio"
                  />
                  <WiredInput
                    name="customPrice"
                    min="0.01"
                    step="0.01"
                    className="c-input"
                    onChange={e => { this.selectCustomPrice(); this.onInputChange(e); }}
                    onClick={this.selectCustomPrice}
                    revalidateOnSubmit
                    validator={(v) => {
                      const priceValue = this.formState.getFieldState('price').getValue();
                      if (!priceValue || (priceValue === 'custom')) {
                        return v.min(0.01).msg('donation.form.errors.customPrice.min');
                      }

                      return v;
                    }}
                  />
                  <span className="donation__custom-price-underline" />
                </span>
                <ValidationMessage msg={errors.customPrice} />
              </label>

              {allowMonthly &&
              <Label className="c-checkbox" onClick={this.onMonthlyLabelClick}>
                <WiredInput
                  type="checkbox"
                  name="isMonthlyDonation"
                  defaultValue={0}
                  className="c-checkbox__input"
                />
                <i className="icon-checkbox c-checkbox__icon"/>
                <strong className="c-checkbox__text">
                  <T msg="donation.labels.wantMonthlyDonation"/>
                </strong>
              </Label>
              }

              <button className="c-button c-button--primary donation__submit">
                <span className="c-button__content">
                  <span className="text-icon">
                    <span className="text-icon__text">
                      <T msg="donation.actions.donate" />
                    </span>
                    <i className="icon-arrow-forward text-icon__icon" />
                  </span>
                </span>
              </button>
            </div>
          </fieldset>
        </FormStateForm>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./app/Assets/js/features/donation/PriceForm.js